import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * LeadConnector chat widget loader.
 * Injects the LeadConnector script on every route EXCEPT /contact.
 * Also force-collapses the widget on first mount so the panel does not
 * auto-open in the user's face — they have to click the launcher.
 */
const LeadConnectorWidget = () => {
  const { pathname } = useLocation();
  const hide = pathname.startsWith('/contact');

  useEffect(() => {
    if (hide) {
      // Remove the loader + any widget DOM the script may have appended.
      document
        .querySelectorAll('script[data-widget-id="6a98c83039356933b8aa7eba"]')
        .forEach((s) => s.remove());
      document
        .querySelectorAll('script[src*="leadconnectorhq.com/chat-widget"]')
        .forEach((s) => s.remove());
      document
        .querySelectorAll('[id^="lc_text-widget"], [class*="lc_text"]')
        .forEach((n) => n.remove());
      return undefined;
    }

    if (!document.querySelector('script[data-widget-id="6a98c83039356933b8aa7eba"]')) {
      const s = document.createElement('script');
      s.src = 'https://widgets.leadconnectorhq.com/loader.js';
      s.async = true;
      s.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
      s.setAttribute('data-widget-id', '6a98c83039356933b8aa7eba');
      s.setAttribute('data-source', 'WEB_USER');
      document.body.appendChild(s);
    }

    // Force-collapse the widget on first mount. LeadConnector's chat widget
    // sometimes auto-opens with a greeting bubble — we want it closed until
    // the user clicks the launcher. This once-only routine waits for the
    // widget custom element to appear, then dispatches a click on any close/
    // minimize button it exposes, and sets a session flag so we don't fight
    // the user if they reopen it later.
    if (sessionStorage.getItem('lc_forced_closed') === '1') return undefined;

    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~10s worst case
    const CLOSE_SELECTORS = [
      '[aria-label*="close" i]',
      '[aria-label*="minimize" i]',
      '[aria-label*="dismiss" i]',
      '[data-testid*="close" i]',
      '.chat-widget-close',
      '.close-button',
    ];

    const tryClose = () => {
      const roots = document.querySelectorAll(
        'lc-chat-widget, chat-widget, [id^="lc_text-widget"], iframe[src*="leadconnectorhq"]'
      );
      let clicked = false;
      roots.forEach((root) => {
        // Some widgets render inside shadow DOM; probe shadow root if present.
        const searchIn = root.shadowRoot || root;
        for (const sel of CLOSE_SELECTORS) {
          const btn = searchIn.querySelector?.(sel);
          if (btn) {
            btn.click();
            clicked = true;
            break;
          }
        }
        // Also try postMessage for iframe widgets
        if (root.tagName === 'IFRAME') {
          try {
            root.contentWindow?.postMessage({ type: 'close-chat-widget' }, '*');
          } catch { /* cross-origin — ignore */ }
        }
      });

      // Inject a one-time CSS shim that hides any "open" state until it stops
      // trying to reopen. Works alongside the click above.
      if (!document.getElementById('lc-force-closed-css') && roots.length) {
        const style = document.createElement('style');
        style.id = 'lc-force-closed-css';
        style.textContent = `
          /* Force-collapse any auto-opened chat panel until user clicks the launcher */
          lc-chat-widget [class*="chat-window"][data-open="true"],
          lc-chat-widget [class*="widget-open"],
          chat-widget [class*="chat-window"][data-open="true"],
          [id^="lc_text-widget"] [class*="chat-window"][data-open="true"] {
            transform: translateY(140%) scale(0.9) !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(style);
        // Remove the shim after ~4s so the launcher's own open logic works
        // as expected once the user actually clicks it.
        setTimeout(() => document.getElementById('lc-force-closed-css')?.remove(), 4000);
      }

      if (clicked) {
        sessionStorage.setItem('lc_forced_closed', '1');
        return true;
      }
      return false;
    };

    const iv = setInterval(() => {
      attempts += 1;
      if (tryClose() || attempts >= MAX_ATTEMPTS) clearInterval(iv);
    }, 250);

    return () => clearInterval(iv);
  }, [hide]);

  return null;
};

export default LeadConnectorWidget;
